import React, { useEffect, useState } from 'react';
import lottie from 'lottie-web';
import useStyles from 'isomorphic-style-loader/useStyles';

import { Summary, Button } from '@sololearnorg/sol';
import { SlIcon, useIcons, bit1Cube } from '@sololearnorg/icons';

import { i18n } from '../../../../symphony';
import { useFadeOut } from '../../SlHooks/useFadeOut';
import { SlTransition } from '../../SlTransition/SlTransition';
import { animateReward } from '../../../private/utils/helpers';

import celebration from '../animations/celebration.json';
import confetti from '../animations/confetti.json';

import s from './SlFirstLessonCompleteCelebration.scss';

export interface ISlFirstLessonCelebrationProps {
    onContinueClick: () => void;
    lessonName: string;
    xp?: number;
    bits?: number;
}

export const SlFirstLessonCompleteCelebration: React.FC<ISlFirstLessonCelebrationProps> = React.memo(
	({
		onContinueClick, xp: xpTotal, bits: bitsTotal, lessonName,
	}) => {
		useStyles(s);
		const [xp, setXp] = useState<number>(0);
		const [bits, setBits] = useState<number>(0);

		const { isFadeOutStarted, startFadeOut } = useFadeOut(600);

		useIcons([bit1Cube]);

		const animateRewards = (): void => {
			const animateXps = (callback?: () => void) => animateReward(0, xpTotal, setXp, callback);

			const animateBits = (callback?: () => void) => animateReward(0, bitsTotal, setBits, callback);

			animateXps(animateBits);
		};

		useEffect(() => {
			lottie.loadAnimation({
				animationData: celebration,
				container: document.getElementById(
					'sl-first-lesson__animation',
				),
				loop: false,
			});
			lottie.loadAnimation({
				animationData: confetti,
				container: document.getElementById(
					'sl-first-lesson__confetti',
				),
				loop: false,
			});
			setTimeout(() => {
				animateRewards();
			}, 1300);
		}, []);

		return (
			<div className="sl-first-lesson__summary">
				<Summary
					illustration={(
						<>
							<SlTransition
								fadeIn={{ delay: 0.1, duration: 0.3 }}
								fadeOut={{ delay: 0, duration: 0.3 }}
								isFadeOutStarted={isFadeOutStarted}
							>
								<div
									className="sl-first-lesson__animation"
									id="sl-first-lesson__animation"
								/>
							</SlTransition>
							<div
								className="sl-first-lesson__confetti"
								id="sl-first-lesson__confetti"
							/>
						</>
					)}
					headline={(
						<SlTransition
							fadeIn={{ delay: 0.5, duration: 0.3 }}
							fadeOut={{ delay: 0.1, duration: 0.3 }}
							isFadeOutStarted={isFadeOutStarted}
						>
							<div className="sl-first-lesson__headline">
								<p>
									{i18n.t(
										'web-streak.first-lesson-complete-title',
									)}
								</p>
							</div>
						</SlTransition>
					)}
					description={(
						<>
							<SlTransition
								fadeIn={{ delay: 0.5, duration: 0.3 }}
								fadeOut={{ delay: 0.1, duration: 0.3 }}
								isFadeOutStarted={isFadeOutStarted}
							>
								<div className="sl-first-lesson__description">
									<p>
										{i18n.t(
											'web-streak.first-lesson-complete-description-1',
											{
												lessonName,
											},
										)}
									</p>
									<p>
										{i18n.t(
											'web-streak.first-lesson-complete-description-2',
										)}
									</p>
								</div>
							</SlTransition>
							<SlTransition
								fadeIn={{ delay: 0.9, duration: 0.3 }}
								fadeOut={{ delay: 0.2, duration: 0.3 }}
								isFadeOutStarted={isFadeOutStarted}
							>
								<div
									className="sl-first-lesson__reward"
									id="sl-first-lesson__reward"
								>
									<p className="sl-first-lesson__reward__item title">
										{i18n.t(
											'web-streak.first-lesson-reward-title',
										)}
									</p>
									<SlTransition
										fadeIn={{
											delay: 1.3,
											duration: 0.5,
										}}
									>
										<div>
											<p className="sl-first-lesson__reward__item value">
												<span className="sl-first-lesson__reward__item number">
													+
													{xp}
												</span>
												XP
											</p>
											<p className="sl-first-lesson__reward__item description">
												{i18n.t(
													'web-streak.first-lesson-reward-xp-info',
												)}
											</p>
										</div>
									</SlTransition>
									<SlTransition
										fadeIn={{
											delay: 2.3,
											duration: 0.5,
										}}
									>
										<div>
											<p className="sl-first-lesson__reward__item value">
												<span className="sl-first-lesson__reward__item number">
													+
													{bits}
                                                    &nbsp;
												</span>
												<span className="sl-first-lesson__reward__item bits-icon">
													<SlIcon iconId="bit1Cube" />
												</span>
											</p>
											<div className="sl-first-lesson__reward__item description">
												Bits (
												<div className="sl-first-lesson__reward__item bits-icon">
													<SlIcon iconId="bit1Cube" />
												</div>
												)
												{' '}
												{i18n.t(
													'web-streak.first-lesson-reward-bits-info',
												)}
											</div>
										</div>
									</SlTransition>
								</div>
							</SlTransition>
						</>
					)}
					actionBar
					actions={[
						<SlTransition
							fadeIn={{ delay: 1.1, duration: 0.5 }}
							fadeOut={{ delay: 0.3, duration: 0.3 }}
							isFadeOutStarted={isFadeOutStarted}
							key={0}
						>
							<Button
								key="Continue"
								onClick={() => startFadeOut(onContinueClick)}
								dataTestid="continueBtn"
							>
								Continue
							</Button>
						</SlTransition>,
					]}
				/>
			</div>
		);
	},
);
